import { ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { FinopsAlert, FinopsAlertStatus } from '../../../../shared/types/finops';
import { AlertSeverityBadge } from './AlertSeverityBadge';
import { deadlineDays, fmtDateUTC } from './alerts-format';

export const ALERT_COLUMNS = 9;

/** "in N days" / "N days ago" / "today", absolute UTC date as the native tooltip. */
export function DeadlineText({ deadline, resolved = false }: { deadline?: string; resolved?: boolean }) {
  const { t } = useTranslation();
  if (!deadline) return <span className="text-muted-foreground">—</span>;
  const days = deadlineDays(deadline);
  const text =
    days === 0
      ? t('finops.alerts.deadline.today')
      : days > 0
        ? t('finops.alerts.deadline.in_days', { count: days })
        : t('finops.alerts.deadline.ago_days', { count: -days });
  const tone = resolved
    ? 'text-muted-foreground'
    : days < 0
      ? 'text-rose-600 dark:text-rose-400'
      : days <= 30
        ? 'text-amber-600 dark:text-amber-400'
        : '';
  return <span className={`whitespace-nowrap ${tone}`} title={fmtDateUTC(deadline)}>{text}</span>;
}

export function ServiceChip({ service }: { service: string }) {
  return (
    <span className="inline-flex rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] text-foreground">
      {service}
    </span>
  );
}

function StatusChip({ status }: { status: FinopsAlertStatus }) {
  const { t } = useTranslation();
  const label = {
    upcoming: t('finops.alerts.status.upcoming'),
    open: t('finops.alerts.status.open'),
    resolved: t('finops.alerts.status.resolved'),
  }[status];
  const tone = status === 'resolved'
    ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
    : 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
  return <span className={`inline-flex rounded-full px-2 py-0.5 text-xs ${tone}`}>{label}</span>;
}

function EntityStatusChip({ status }: { status: string }) {
  const tone =
    status === 'PENDING'
      ? 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
      : status === 'RESOLVED'
        ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
        : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300';
  return <span className={`inline-flex rounded px-1.5 py-0.5 font-mono text-[10px] ${tone}`}>{status}</span>;
}

function Meta({ label, value }: { label: string; value?: string }) {
  return (
    <div>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-mono break-all">{value || '—'}</dd>
    </div>
  );
}

export function AlertRow({
  alert, expanded, onToggle, focused = false, rowRef,
}: {
  alert: FinopsAlert;
  expanded: boolean;
  onToggle: () => void;
  focused?: boolean;
  rowRef?: (el: HTMLTableRowElement | null) => void;
}) {
  const { t } = useTranslation();
  const resolved = alert.status === 'resolved';
  const entities = alert.entities ?? [];

  return (
    <>
      <tr
        ref={rowRef}
        onClick={onToggle}
        className={`border-b cursor-pointer hover:bg-muted/40 ${expanded ? 'bg-muted/30' : ''} ${
          focused ? 'ring-2 ring-inset ring-[#1CC9B9]' : ''
        }`}
      >
        <td className="py-2 pl-2 pr-1 w-8">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onToggle(); }}
            aria-expanded={expanded}
            aria-label={expanded ? t('finops.alerts.row.collapse') : t('finops.alerts.row.expand')}
            className="flex h-6 w-6 items-center justify-center rounded hover:bg-muted"
          >
            <ChevronRight className={`h-4 w-4 transition-transform ${expanded ? 'rotate-90' : ''}`} />
          </button>
        </td>
        <td className="py-2 pr-3"><AlertSeverityBadge severity={alert.severity} /></td>
        <td className="pr-3"><ServiceChip service={alert.service} /></td>
        <td className="pr-3 font-medium">{alert.title}</td>
        <td className="pr-3">
          <div>{alert.accountName}</div>
          {alert.accountName !== alert.accountId && (
            <div className="text-xs text-muted-foreground font-mono">{alert.accountId}</div>
          )}
        </td>
        <td className="pr-3"><DeadlineText deadline={alert.deadline} resolved={resolved} /></td>
        <td className="pr-3 text-right tabular-nums">{alert.pendingCount ?? '—'}</td>
        <td className="pr-3"><StatusChip status={alert.status} /></td>
        <td className="pr-3 whitespace-nowrap text-muted-foreground">
          {alert.lastUpdatedTime ? fmtDateUTC(alert.lastUpdatedTime) : '—'}
        </td>
      </tr>

      {expanded && (
        <tr className="border-b bg-muted/20">
          <td colSpan={ALERT_COLUMNS} className="px-4 py-4">
            <div className="grid gap-4 lg:grid-cols-3">
              <div className="lg:col-span-2 space-y-4 min-w-0">
                <section>
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                    {t('finops.alerts.row.description')}
                  </h4>
                  <p className="text-sm whitespace-pre-line max-h-64 overflow-y-auto">
                    {alert.description || '—'}
                  </p>
                </section>
                <section>
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                    {t('finops.alerts.row.entities')}
                  </h4>
                  {entities.length === 0 ? (
                    <p className="text-sm text-muted-foreground">{t('finops.alerts.row.no_entities')}</p>
                  ) : (
                    <ul className="space-y-1 max-h-64 overflow-y-auto">
                      {entities.map((e) => (
                        <li key={e.value} className="flex items-center gap-2 text-xs">
                          <EntityStatusChip status={e.status} />
                          <span className="font-mono break-all">{e.value}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              </div>
              <dl className="space-y-2 text-xs">
                <Meta label={t('finops.alerts.row.event_type')} value={alert.eventTypeCode} />
                <Meta label={t('finops.alerts.row.region')} value={alert.region} />
                <Meta label={t('finops.alerts.row.actionability')} value={alert.actionability} />
                <Meta label={t('finops.alerts.row.personas')} value={alert.personas?.join(', ')} />
                <Meta
                  label={t('finops.alerts.row.last_updated')}
                  value={alert.lastUpdatedTime ? fmtDateUTC(alert.lastUpdatedTime) : undefined}
                />
              </dl>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
